import { useEffect, useRef, useState } from 'react';
import css from './VideoScanningOverlay.module.scss';

type VideoScanningOverlayProps = {
  active: boolean;
};

export const VideoScanningOverlay = ({ active }: VideoScanningOverlayProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const fadeOutTimerRef = useRef<number | null>(null);
  const startPosRef = useRef({ x: 50, y: 50 });
  const targetPosRef = useRef({ x: 50, y: 50 });
  const currentPosRef = useRef({ x: 50, y: 50 });
  const startSizeRef = useRef(225);
  const targetSizeRef = useRef(225);
  const currentSizeRef = useRef(225);
  const startTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(400);
  const pauseUntilRef = useRef<number>(0);
  const needsNewTargetRef = useRef(false);

  // Handle visibility and fade-out
  useEffect(() => {
    if (active) {
      setIsVisible(true);
      setIsFadingOut(false);
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    } else {
      setIsFadingOut(true);
      fadeOutTimerRef.current = window.setTimeout(() => {
        setIsVisible(false);
        setIsFadingOut(false);
      }, 300); // Match fade-out animation duration
    }

    return () => {
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    };
  }, [active]);

  useEffect(() => {
    if (!active) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    // Custom easing function - simulates human camera movement (less smooth)
    const humanCameraEasing = (t: number): number => {
      // Custom cubic bezier approximation for more organic, jittery movement
      const p0 = 0,
        p1 = 0.45,
        p2 = 0.55,
        p3 = 1;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;
      return p0 * mt3 + 3 * p1 * mt2 * t + 3 * p2 * mt * t2 + p3 * t3;
    };

    // Generate a new random target position and size
    const generateNewTarget = (currentTime: number) => {
      startPosRef.current = { ...currentPosRef.current };
      startSizeRef.current = currentSizeRef.current;
      targetPosRef.current = {
        x: 15 + Math.random() * 70, // Keep within 15-85% range
        y: 15 + Math.random() * 70,
      };
      targetSizeRef.current = 180 + Math.random() * 120; // 180-300px range (1.5x bigger)
      startTimeRef.current = currentTime;
      durationRef.current = 333 + Math.random() * 334; // 333-667ms (2x faster)
      needsNewTargetRef.current = false;
    };

    // Initialize first target
    generateNewTarget(performance.now());

    const animate = () => {
      const currentTime = performance.now();

      // Check if we need to generate a new target after pause
      if (needsNewTargetRef.current && currentTime >= pauseUntilRef.current) {
        generateNewTarget(currentTime);
      }

      // Check if we're in a pause - hold position and pulse size
      if (currentTime < pauseUntilRef.current) {
        // Pulse the spotlight size during pause based on current target size
        const pulsePhase = (currentTime % 1000) / 1000; // 1 second cycle
        const pulseSize = currentSizeRef.current + Math.sin(pulsePhase * Math.PI * 2) * 15; // ±15px pulse
        if (spotlightRef.current) {
          spotlightRef.current.style.setProperty('--spotlight-size', `${pulseSize}px`);
        }
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / durationRef.current, 1);

      if (progress >= 1 && !needsNewTargetRef.current) {
        // Reached target, set variable pause and flag for new target
        const pauseDuration = 200 + Math.random() * 600; // 200-800ms variable pause
        pauseUntilRef.current = currentTime + pauseDuration;
        needsNewTargetRef.current = true;
      }

      // Apply human-like easing
      const easedProgress = humanCameraEasing(progress);

      // Interpolate position
      const newX =
        startPosRef.current.x + (targetPosRef.current.x - startPosRef.current.x) * easedProgress;
      const newY =
        startPosRef.current.y + (targetPosRef.current.y - startPosRef.current.y) * easedProgress;

      // Interpolate size - move towards target size
      const newSize =
        startSizeRef.current + (targetSizeRef.current - startSizeRef.current) * easedProgress;

      currentPosRef.current = { x: newX, y: newY };
      currentSizeRef.current = newSize;

      // Update DOM directly instead of triggering React re-renders
      if (spotlightRef.current) {
        spotlightRef.current.style.setProperty('--spotlight-x', `${newX}%`);
        spotlightRef.current.style.setProperty('--spotlight-y', `${newY}%`);
        spotlightRef.current.style.setProperty('--spotlight-size', `${newSize}px`);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (fadeOutTimerRef.current) {
        clearTimeout(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    };
  }, [active]);

  if (!isVisible) return null;

  return (
    <div className={`${css.overlay} ${isFadingOut ? css.fadeOut : ''}`}>
      <div
        ref={spotlightRef}
        className={css.spotlight}
        style={
          {
            '--spotlight-x': '50%',
            '--spotlight-y': '50%',
            '--spotlight-size': '225px',
          } as React.CSSProperties
        }
      />
    </div>
  );
};
